import React, { useState, useEffect } from 'react';

import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import InfiniteScroll from 'react-infinite-scroller';

import {
  searchShopsByText,
  countShopsByText,
} from '@/api/fetch/marketplace-api';
import { Location } from '@/api/gen-protos/ads_pb';
import {
  SearchUsersByTitleRequest,
  PaginationParams,
  MatchParams,
} from '@/api/gen-protos/discovery_pb';
import { LIMIT } from '@/lib/constants';
import { getDelegationsOfGovernorate, governorates } from '@/lib/helpers';
import { cleanObject } from '@/lib/utils';
import Layout from '@/modules/core/components/Layout';
import { mapUserResponseToIUserData } from '@/modules/core/mappings';
import { IUserData } from '@/modules/core/types';
import InlistShop from '@/tayara-kit/banners/InListShop';
import PublisherCard from '@/tayara-kit/PublisherCard';

interface ShopsProps {
  shops: IUserData[];
  countShops: number;
}

const Neuf = ({ shops, countShops }: ShopsProps) => {
  const [shopList, setShopList] = useState<IUserData[]>(shops);
  const [page, setPage] = useState(0);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  const [selectedGovernorate, setSelectedGovernorate] = useState<string | null>(
    null
  );
  const [delegations, setDelegations] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedDelegation, setSelectedDelegation] = useState<string | null>(
    null
  );
  const [title, setTitle] = useState<string>('');
  const loadMoreShopsList = async (reset: boolean) => {
    try {
      const newPage = reset ? 0 : page + 1;

      const paginationParams = new PaginationParams();
      paginationParams.setLimit(LIMIT);
      paginationParams.setSkip(newPage * LIMIT);

      const matchParams = new MatchParams();
      matchParams.setPaginationparams(paginationParams);

      const location = new Location();
      if (selectedGovernorate) {
        location.setGovernorate(selectedGovernorate);
      }
      if (selectedDelegation) {
        location.setDelegation(selectedDelegation);
      }

      const searchShopRequest = new SearchUsersByTitleRequest();
      searchShopRequest.setMatchparams(matchParams);
      searchShopRequest.setLocation(location);
      if (title !== '') {
        searchShopRequest.setTitle(title);
      }
      searchShopRequest.setSubcategoryid('66c34239d16d7f3b0c2ec56e');

      const newShops = await searchShopsByText('', searchShopRequest);
      const mappedNewShops = newShops.map((shop) =>
        mapUserResponseToIUserData(shop)
      );

      if (reset) {
        setShopList(mappedNewShops);
      } else {
        setShopList((prev) => [...prev, ...mappedNewShops]);
      }

      setPage(newPage);
    } catch (error) {
      console.error('Failed to load more shops:', error);
    }
  };
  const handleTitleChange = () => {
    if (title !== '') {
      loadMoreShopsList(true);
    }
  };

  useEffect(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    if (selectedGovernorate) {
      const delegationList = getDelegationsOfGovernorate(selectedGovernorate);
      setDelegations(delegationList);
      setSelectedDelegation(null); // Reset delegation when governorate changes
    } else {
      setDelegations([]);
    }
  }, [selectedGovernorate]);

  useEffect(() => {
    loadMoreShopsList(true); // Reload the shops list when filters change
  }, [selectedGovernorate, selectedDelegation]);

  const GreenIndexes = [shopList?.length];

  return (
    <div className=" min-h-screen py-8">
      <div className="relative">
        <img
          src="https://storage.googleapis.com/caravel-images/home.jpg"
          alt="Cover"
          className="w-full  h-96 object-cover rounded-2xl "
        />
        <div className="absolute inset-0 bg-opacity-10 flex flex-col items-center justify-center">
          <h1 className="text-white text-4xl font-bold font-family-inherit animate-fadeIn">
            <span className="text-4xl md:text-5xl lg:text-7xl font-black text-white !text-start leading-none neons mt-14">
              ImmoNeuf
            </span>
          </h1>
          <h2 className="text-white text-4xl top-10 font-bold font-family-inherit text-center animate-fadeIn">
            <span className="animate-shake">
              Tout L’immobilier Neuf en Tunisie en 1 Clic !
            </span>
          </h2>
        </div>
      </div>

      <div className="w-full py-8">
        {/* Filter UI */}
        <div className="flex flex-wrap items-center justify-center flex-col  sm:flex-row md:justify-end gap-x-2 my-3">
          <select
            value={selectedGovernorate || ''}
            onChange={(e) => setSelectedGovernorate(e.target.value)}
            className="select-sm w-full md:w-fit h-[40px] pl-10 max-w-full max-h-full select rounded-md focus:outline-none border-1 border-gray-300 bg-gray-200/70 text-xs !text-gray-600 hover:!text-gray-700 !font-bold"
          >
            <option value="">Select Governorate</option>
            {governorates.map((gov) => (
              <option key={gov.id} value={gov.id}>
                {gov.name}
              </option>
            ))}
          </select>

          <select
            value={selectedDelegation || ''}
            onChange={(e) => setSelectedDelegation(e.target.value)}
            className="select-sm w-full md:w-fit h-[40px] pl-10 max-w-full max-h-full select rounded-md focus:outline-none border-1 border-gray-300 bg-gray-200/70 text-xs !text-gray-600 hover:!text-gray-700 !font-bold"
            disabled={!delegations.length}
          >
            <option value="">Select Delegation</option>
            {delegations.map((del) => (
              <option key={del.id} value={del.id}>
                {del.name}
              </option>
            ))}
          </select>
          <div className="relative flex h-10 w-full min-w-[200px] max-w-[24rem]">
            <button
              className="!absolute right-1 top-1 z-10 select-none rounded bg-neuf-200 py-2 px-4 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md shadow-blue-500/20 transition-all hover:shadow-lg hover:shadow-blue-500/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none peer-placeholder-shown:pointer-events-none peer-placeholder-shown:bg-blue-gray-500 peer-placeholder-shown:opacity-50 peer-placeholder-shown:shadow-none"
              type="button"
              data-ripple-light="true"
              onClick={() => handleTitleChange()}
            >
              recherche
            </button>
            <input
              type="text"
              className="peer h-full w-full rounded-[7px] border border-blue-gray-200 bg-transparent px-3 py-2.5 pr-20 font-sans text-sm font-normal text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-blue-900 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50"
              placeholder=" "
              required
              onChange={(e) => setTitle(e.target.value)}
            />
            <label className="before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-normal leading-tight text-blue-gray-400 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-blue-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-blue-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-blue-500 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:!border-blue-500 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:!border-blue-500 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500">
              Chercher un projet
            </label>
          </div>
        </div>

        <InfiniteScroll
          pageStart={0}
          loadMore={() => loadMoreShopsList(false)}
          hasMore={shopList.length < countShops}
          loader={
            <div className="loader" key={0}>
              {''}
            </div>
          }
        >
          <div
            className={`w-full flex-wrap flex justify-center xl:justify-start items-start gap-x-4 gap-y-8 mt-4`}
          >
            {shopList.map((publisher, i) =>
              GreenIndexes.includes(i) && windowSize.width > 1024 ? (
                <InlistShop key={i} />
              ) : (
                <PublisherCard
                  key={i}
                  userDetails={publisher}
                  NeufPage={true}
                />
              )
            )}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default Neuf;

Neuf.getLayout = function getLayout(page: JSX.Element) {
  return (
    <Layout>
      <Layout.Wide>{page}</Layout.Wide>
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const paginationParams = new PaginationParams();
  paginationParams.setLimit(LIMIT);
  paginationParams.setSkip(0);

  const matchParams = new MatchParams();
  matchParams.setPaginationparams(paginationParams);
  const searchShopRequest = new SearchUsersByTitleRequest();
  searchShopRequest.setMatchparams(matchParams);
  searchShopRequest.setSubcategoryid('66c34239d16d7f3b0c2ec56e');
  const [shops, countShops] = await Promise.all([
    searchShopsByText('', searchShopRequest),
    countShopsByText(searchShopRequest),
  ]);
  const mappedShops = shops.map(mapUserResponseToIUserData);

  return {
    props: cleanObject({
      ...(await serverSideTranslations(locale || 'en')),
      shops: mappedShops,
      countShops: countShops.getCount(),
    }),
  };
};
